import React, {useEffect} from 'react';

import {useStores} from '../../../../stores/use-stores';

/**
 * Mock user data for Storybook stories
 * Pre-fills userStore with realistic test data for Update/Collect Missing Details scenarios
 */
export const mockUserData = {
  firstName: 'John',
  lastName: 'Smith',
  emailAddress: 'john.smith@example.com',
  dateOfBirth: new Date('1985-03-15'),
  phoneNumber: '0412345678',
  billingAddress: {
    lines: ['123 Main Street'],
    locality: 'Sydney',
    administrative_area: 'NSW',
    postal_code: '2000',
  },
  isLoggedIn: true,
};

/**
 * Mock signup offers data for Storybook stories
 */
export const mockSignUpOffers = [
  {
    id: '1',
    title: 'Welcome Offer',
    description: 'Get 10% off your first order',
    code: 'WELCOME10',
  },
  {
    id: '2',
    title: 'Birthday Bonus',
    description: 'Special birthday discount',
    code: 'BDAY2025',
  },
];

/**
 * Decorator that injects mock store data for Storybook stories
 *
 * Usage in stories:
 * ```typescript
 * export const UpdateWithData: Story = {
 *   decorators: [WithMockStoreData],
 *   render: () => <Component />
 * };
 * ```
 */
export const WithMockStoreData = (Story: React.ComponentType) => {
  return () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {userStore, offersStore} = useStores();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      // Inject mock user data into singleton userStore
      Object.assign(userStore, mockUserData);

      // Inject mock signup offers into singleton offersStore
      // @ts-expect-error - accessing internal property for Storybook mocking
      offersStore._signUpOfferData = mockSignUpOffers;

      // eslint-disable-next-line no-console
      console.log('[Storybook Mock] User data injected:', {
        firstName: userStore.firstName,
        lastName: userStore.lastName,
        email: userStore.emailAddress,
        isLoggedIn: userStore.isLoggedIn,
      });

      // eslint-disable-next-line no-console
      console.log('[Storybook Mock] Signup offers injected:', mockSignUpOffers);

      // Cleanup on unmount
      return () => {
        // Reset to empty state
        userStore.firstName = '';
        userStore.lastName = '';
        userStore.emailAddress = '';
        userStore.dateOfBirth = null;
        userStore.phoneNumber = '';
        userStore.billingAddress = null;
        // @ts-expect-error - Resetting internal property for Storybook cleanup
        userStore._isLoggedIn = false;
        // @ts-expect-error - Resetting internal property for Storybook cleanup
        offersStore._signUpOfferData = null;
      };
    }, [userStore, offersStore]);

    return <Story />;
  };
};
